import React from 'react';
import ProfileImageField from './ProfileImageField';
import ThemedSelect from '../ui/ThemedSelect';
import { EMPLOYMENT_TYPE } from '../../utils/profileFormMaps';

const Field = ({ label, optional, children, className = '' }) => (
    <label className={`block space-y-2 ${className}`}>
        <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
            {label}
            {optional ? (
                <span className="font-normal text-zinc-400"> (optional)</span>
            ) : null}
        </span>
        {children}
    </label>
);

const EmployeeProfileFormFields = ({
    form,
    onChange,
    inputClass,
    backendToken,
    walletAddress = '',
    disabled = false,
    showWallet = false,
    showStatus = false,
    showCompensation = true,
}) => {
    const setField = (name, value) => {
        onChange({ target: { name, value } });
    };

    const employmentType = form.employmentType || EMPLOYMENT_TYPE.FREELANCE;
    const isSalaried = employmentType === EMPLOYMENT_TYPE.SALARIED;

    return (
        <div className="space-y-5">
            <ProfileImageField
                label="Profile photo"
                value={form.profileImageUrl}
                onChange={(url) => setField('profileImageUrl', url)}
                walletAddress={walletAddress || form.wallet}
                backendToken={backendToken}
                disabled={disabled}
            />

            <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name">
                    <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={onChange}
                        placeholder="Alex Rivera"
                        className={inputClass}
                        disabled={disabled}
                    />
                </Field>
                {showWallet ? (
                    <Field label="Wallet address">
                        <input
                            type="text"
                            name="wallet"
                            required
                            value={form.wallet}
                            onChange={onChange}
                            placeholder="0x…"
                            className={`${inputClass} font-mono text-sm`}
                            disabled={disabled}
                        />
                    </Field>
                ) : null}
                <Field label="Email" optional>
                    <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="name@email.com"
                        className={inputClass}
                        disabled={disabled}
                    />
                </Field>
                <Field label="Job title" optional>
                    <input
                        type="text"
                        name="jobTitle"
                        value={form.jobTitle}
                        onChange={onChange}
                        placeholder="Smart contract engineer"
                        className={inputClass}
                        disabled={disabled}
                    />
                </Field>
                <Field label="Department" optional>
                    <input
                        type="text"
                        name="department"
                        value={form.department}
                        onChange={onChange}
                        placeholder="Engineering"
                        className={inputClass}
                        disabled={disabled}
                    />
                </Field>
                <Field label="Phone" optional>
                    <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="+1 555 0100"
                        className={inputClass}
                        disabled={disabled}
                    />
                </Field>
                {showStatus ? (
                    <Field label="Status">
                        <ThemedSelect
                            name="status"
                            value={form.status}
                            onChange={onChange}
                            disabled={disabled}
                            options={[
                                { value: 'active', label: 'Active' },
                                { value: 'inactive', label: 'Inactive' },
                            ]}
                        />
                    </Field>
                ) : null}
                {showCompensation ? (
                    <>
                        <Field label="Employment type">
                            <ThemedSelect
                                name="employmentType"
                                value={employmentType}
                                onChange={onChange}
                                disabled={disabled}
                                options={[
                                    {
                                        value: EMPLOYMENT_TYPE.FREELANCE,
                                        label: 'Freelance — paid per task',
                                    },
                                    {
                                        value: EMPLOYMENT_TYPE.SALARIED,
                                        label: 'Salaried — fixed payroll',
                                    },
                                ]}
                            />
                        </Field>
                        {isSalaried ? (
                            <Field label="Monthly salary (USDC)">
                                <input
                                    type="number"
                                    min="0.01"
                                    step="any"
                                    name="monthlySalary"
                                    value={form.monthlySalary}
                                    onChange={onChange}
                                    placeholder="5000.00"
                                    className={inputClass}
                                    disabled={disabled}
                                    required
                                />
                            </Field>
                        ) : (
                            <Field label="Hourly rate (USDC)" optional>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    name="hourlyRate"
                                    value={form.hourlyRate}
                                    onChange={onChange}
                                    placeholder="0.00"
                                    className={inputClass}
                                    disabled={disabled}
                                />
                            </Field>
                        )}
                        <div className="sm:col-span-2 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-xs text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-400">
                            {isSalaried ? (
                                <>
                                    <strong className="font-semibold text-zinc-800 dark:text-zinc-200">
                                        Salaried:
                                    </strong>{' '}
                                    Enable on-chain payroll after adding this employee. Recurring
                                    USDC claims use the Payroll facet (not per-task escrow).
                                </>
                            ) : (
                                <>
                                    <strong className="font-semibold text-zinc-800 dark:text-zinc-200">
                                        Freelance:
                                    </strong>{' '}
                                    Assign tasks with lump-sum or milestone escrow. Payment is
                                    released per task or per milestone on the Job Escrow facet.
                                </>
                            )}
                        </div>
                    </>
                ) : null}
            </div>

            <Field label="Short bio" optional>
                <textarea
                    name="bio"
                    rows={3}
                    value={form.bio}
                    onChange={onChange}
                    placeholder="Skills, timezone, or how you prefer to work…"
                    className={`${inputClass} resize-y min-h-[80px]`}
                    disabled={disabled}
                />
            </Field>
        </div>
    );
};

export default EmployeeProfileFormFields;
